import React, { useEffect, useRef, useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { Button } from '../src/components/ui/button';
import { Banner } from '../src/components/ui/banner';
import { Input } from '../src/components/ui/input';
import { useFocusTrap } from '../src/hooks/useFocusTrap';

/**
 * ⭐ THE CONFIRMATION WORD (operator-ruled 2026-08-17, wording approved verbatim before build).
 * Previously the user re-typed their own EMAIL ADDRESS. A long address wrapped onto a second
 * line inside a narrow modal, so the instruction — the one sentence that has to be read on an
 * irreversible action — was the part that broke. A fixed word is the same length for every
 * user, so the layout cannot depend on who is signed in.
 *
 * ⚠️ CASE-SENSITIVE, DELIBERATELY. The point of this step is a deliberate act; lowercase
 * "delete" is what a person types by reflex. The old check lower-cased both sides, which was
 * right for an email (case-insensitive by RFC) and wrong for a confirmation token.
 *
 * ⚠️ CONSEQUENCE, RECORDED RATHER THAN DISCOVERED: this removed the last use of the modal's
 * `userEmail` prop, so the dialog no longer names WHICH account is being deleted. That follows
 * from the approved wording and was flagged to the operator. The account is the signed-in one
 * and the dialog is reached from that account's own settings page, so the identity is implicit
 * — but if a future ruling wants it shown again, it is a display concern, NOT a reason to make
 * the user type it.
 */
const CONFIRMATION_WORD = 'DELETE';

interface DeleteAccountModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirmDelete: () => Promise<void>;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
    isOpen,
    onClose,
    onConfirmDelete
}) => {
    const [step, setStep] = useState<1 | 2>(1);
    const [confirmInput, setConfirmInput] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // The account can only be deleted once the user types the confirmation word exactly —
    // a lightweight proof-of-presence gate on this irreversible action. Surrounding
    // whitespace is forgiven (it carries no intent); CASE IS NOT (it carries all of it).
    const confirmationMatches = confirmInput.trim() === CONFIRMATION_WORD;
    const panelRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    // Reset to a clean state each time the modal opens, regardless of how the
    // previous session ended (e.g. a failed deletion left step 2 + an error).
    useEffect(() => {
        if (!isOpen) return;
        setStep(1);
        setConfirmInput('');
        setError(null);
        setIsDeleting(false);
    }, [isOpen]);

    // Modal focus management: initial focus, Tab/Shift+Tab trap, focus restore, and
    // Escape (but not mid-deletion). onEscape reads the latest isDeleting via the hook.
    useFocusTrap({
        active: isOpen,
        containerRef: panelRef,
        initialFocusRef: closeButtonRef,
        // Route through handleCancel (not bare onClose) so Escape resets the step
        // like the X button and backdrop do — otherwise an Escape at step 2 leaves
        // step===2 behind, and the next open mounts the UI-31 live region already
        // holding the permanence warning (announced spuriously by some SRs) plus a
        // one-frame step-2 flash. Closure is safe: evaluated at call time.
        onEscape: () => { if (!isDeleting) handleCancel(); },
    });

    if (!isOpen) return null;

    const handleContinue = () => {
        setStep(2);
    };

    const handleCancel = () => {
        setStep(1);
        setConfirmInput('');
        setError(null);
        onClose();
    };

    const handleBack = () => {
        setStep(1);
        setConfirmInput('');
        setError(null);
    };

    const handleDelete = async () => {
        // Re-entry guard: the Button's isLoading-disable only holds while the
        // primitive's disabled wiring does — belt-and-braces so a second click
        // mid-await can never fire a second concurrent account deletion.
        if (!confirmationMatches || isDeleting) return;

        setIsDeleting(true);
        setError(null);

        try {
            await onConfirmDelete();
            // Parent will handle redirect
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete account. Please try again.');
            setIsDeleting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50"
                onClick={isDeleting ? undefined : handleCancel}
            />

            {/* Modal */}
            {/* UI-05: max-h + internal scroll (FilterModal's proven pattern) — this
                dialog guards an IRREVERSIBLE action, the worst place for its confirm
                controls to sit off-screen on a short viewport. Header stays fixed;
                the body (which follows) becomes the scroll region. */}
            <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="delete-account-title" className="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-border-default">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-state-danger-subtle flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-state-danger" />
                        </div>
                        <h2 id="delete-account-title" className="text-lg font-semibold text-text-primary">
                            Delete your account?
                        </h2>
                    </div>
                    {/* UI-13: modal close kept as raw <button> rather than IconButton —
                        house convention is a tight ~36px X with text-shift hover; the
                        primitive's 40px+ min-size and bg-fill hover would dominate the
                        header. It already carries the harmonised focus-visible ring. */}
                    {!isDeleting && (
                        <button
                            ref={closeButtonRef}
                            onClick={handleCancel}
                            aria-label="Close"
                            className="p-2 rounded-md text-text-subtle hover:text-text-primary transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-primary"
                        >
                            <X size={20} />
                        </button>
                    )}
                </div>

                {/* UI-31: announce the step 1→2 transition. The region is rendered on
                    both steps (a live region must pre-exist its content change to fire
                    reliably); on step 2 it mirrors the on-screen instruction verbatim so
                    screen-reader users hear that the action is permanent and what they
                    must type to confirm. sr-only — zero visual change.
                    ⚠️ MIRRORS THE VISIBLE COPY THROUGH THE SAME CONSTANT — if the two ever
                    diverge, a screen-reader user is told to type something the screen does
                    not show. Both sides read CONFIRMATION_WORD for that reason. */}
                <div aria-live="polite" className="sr-only">
                    {step === 2 && (
                        <>This is permanent and cannot be undone. Type {CONFIRMATION_WORD} to confirm.</>
                    )}
                </div>

                {/* Content — UI-05: the scroll region (see panel comment above) */}
                <div className="px-6 py-5 flex-1 min-h-0 overflow-y-auto">
                    {step === 1 ? (
                        <>
                            <p className="text-text-primary mb-4">
                                This will permanently delete your account and all associated data, including:
                            </p>
                            <ul className="text-text-primary space-y-2 mb-4 ml-1">
                                <li className="flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-text-primary" />
                                    saved gyms
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-text-primary" />
                                    saved searches
                                </li>
                            </ul>
                            <p className="text-text-primary font-medium">
                                This action cannot be undone.
                            </p>
                        </>
                    ) : (
                        <>
                            {error && (
                                <Banner variant="danger" className="mb-4">
                                    {error}
                                </Banner>
                            )}
                            {/* ⭐ TWO SENTENCES ON TWO LINES, operator-approved verbatim. Kept as
                                separate <p> elements rather than one wrapped sentence so the
                                warning and the instruction cannot be read as one breath, and so
                                neither can wrap unpredictably with the signed-in user's identity. */}
                            <p className="text-text-primary mb-1">
                                This is permanent and cannot be undone.
                            </p>
                            <p className="text-text-primary mb-3">
                                Type <span className="font-semibold">{CONFIRMATION_WORD}</span> to confirm.
                            </p>
                            <label htmlFor="delete-confirm-input" className="sr-only">
                                Type {CONFIRMATION_WORD} to confirm
                            </label>
                            <Input
                                id="delete-confirm-input"
                                type="text"
                                value={confirmInput}
                                onChange={(e) => setConfirmInput(e.target.value)}
                                placeholder={CONFIRMATION_WORD}
                                disabled={isDeleting}
                                autoComplete="off"
                                autoFocus
                            />
                        </>
                    )}
                </div>

                {/* Footer — UI-05 review polish: border-t mirrors the header's
                    separator so the pinned confirm row reads as pinned once the
                    body actually scrolls; flex-shrink-0 keeps it out of the
                    flex-column's space negotiation. */}
                <div className="px-6 py-4 flex gap-3 flex-shrink-0 border-t border-border-default">
                    {step === 1 ? (
                        <>
                            <Button
                                variant="ghost"
                                className="flex-1 bg-surface-canvas hover:bg-border-default"
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="danger"
                                className="flex-1"
                                onClick={handleContinue}
                            >
                                Review deletion
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="ghost"
                                className="flex-1 bg-surface-canvas hover:bg-border-default"
                                onClick={handleBack}
                                disabled={isDeleting}
                            >
                                Back
                            </Button>
                            <Button
                                variant="danger"
                                className="flex-1"
                                onClick={handleDelete}
                                disabled={!confirmationMatches}
                                isLoading={isDeleting}
                            >
                                Permanently delete account
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeleteAccountModal;
